import { memo, useCallback, useRef, useState } from "react";
import TagGroupEditor from "./TagGroupEditor";

/**
 * The tags cell. A KPI's tags are a list of paths, not a flat set of labels,
 * so each path is one line — its group name, then its values joined by `›` —
 * and opens an editor in place when clicked.
 *
 * Collapsing matters: KPI 6697 carries four paths of three to four pairs each.
 * Rendered as fourteen loose chips the row would be taller than the screen and
 * the structure would be lost.
 *
 * @param {object} props
 * @param {{ name: string|null, pairs: { key: string, value: string }[] }[]} props.groups
 * @param {"object"|"array"} props.shape which storable form this row uses
 * @param {string[]} props.tagKeys
 * @param {Record<string, string[]>} props.tagValuesByKey
 * @param {boolean} [props.dirty]
 * @param {boolean} [props.disabled]
 * @param {(groups: object[]) => void} props.onChange
 */
function TagGroupsCell({ groups, shape, tagKeys, tagValuesByKey, dirty, disabled, onChange }) {
  const [openIndex, setOpenIndex] = useState(-1);
  const cellRef = useRef(null);
  const close = useCallback(() => setOpenIndex(-1), []);

  const replaceGroup = (index, group) => onChange(groups.map((g, i) => (i === index ? group : g)));

  const removeGroup = (index) => {
    onChange(groups.filter((g, i) => i !== index));
    close();
  };

  const addGroup = () => {
    onChange([...groups, { name: shape === "object" ? "" : null, pairs: [{ key: tagKeys[0] || "", value: "" }] }]);
    setOpenIndex(groups.length);
  };

  return (
    <div className={`kgb-tagcell${dirty ? " kgb-chipcell--dirty" : ""}`} ref={cellRef}>
      {groups.map((group, index) => {
        const summary = group.pairs.map((pair) => pair.value).filter(Boolean).join(" › ");
        return (
          <span className="kgb-taggroup" key={`${group.name ?? "path"}-${index}`}>
            <button
              type="button"
              className="kgb-taggroup-chip"
              disabled={disabled}
              aria-expanded={openIndex === index}
              title={group.pairs.map((pair) => `${pair.key}: ${pair.value}`).join("\n")}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            >
              {group.name ? <span className="kgb-chip-key">{group.name}</span> : null}
              <span className="kgb-taggroup-path">{summary || "empty path"}</span>
            </button>
            {openIndex === index ? (
              <TagGroupEditor
                anchorRef={cellRef}
                group={group}
                named={shape === "object"}
                tagKeys={tagKeys}
                tagValuesByKey={tagValuesByKey}
                onChange={(next) => replaceGroup(index, next)}
                onRemove={() => removeGroup(index)}
                onClose={close}
              />
            ) : null}
          </span>
        );
      })}

      {disabled ? null : (
        <button type="button" className="kgb-add-link" onClick={addGroup}>
          + Add tag path
        </button>
      )}
    </div>
  );
}

export default memo(TagGroupsCell);
